import { useState, useEffect, useMemo } from 'react';
import { useOnboardingStore, UploadedMethodType, type PreviousProduct } from '../../../../../store/onboardingStore';
import { useGenericImageUpload } from '../../../../../hooks/useGenericImageUpload';
import { FOLDER_IMAGES, MAX_FILE_SIZE } from '../../../../onboarding/const/products';
import type { AdditionalOffers } from '../../../../../types/onboarding';
import type { BumpOfferState } from '../types';

function getInitialBumpOffer(offers: AdditionalOffers): BumpOfferState {
  const b = offers.bumpOffer;
  return {
    enabled: b?.enabled ?? false,
    productName: b?.productName ?? '',
    price: b?.price ?? '0.00',
    description: b?.description ?? '',
    image: b?.image,
    isManualEntry: b?.isManualEntry ?? false,
    productLink: b?.productLink ?? '',
    externalId: b?.externalId ?? null,
    providerId: b?.providerId ?? null,
    url_images: b?.url_images,
    isLoading: false,
    error: '',
    uploadedMethod: b?.uploadedMethod ?? UploadedMethodType.NONE,
    previousProductSnapshot: b?.previousProductSnapshot ?? null,
  };
}

export function useBumpOffer() {
  const { data, updateAdditionalOffers } = useOnboardingStore();
  const { additionalOffers } = data;

  const [bumpOffer, setBumpOffer] = useState<BumpOfferState>(() => getInitialBumpOffer(additionalOffers));
  const [method, setMethod] = useState<'link' | 'manual' | 'previous' | null>(null);

  const currentImages = useMemo(
    () => (bumpOffer.url_images ? [bumpOffer.url_images] : []),
    [bumpOffer.url_images]
  );

  const imageUpload = useGenericImageUpload({
    folder: FOLDER_IMAGES,
    currentImages,
    onImagesUpdate: (images) => {
      const imageUrl = images[0];
      const updated: BumpOfferState = {
        ...bumpOffer,
        image: undefined,
        uploadedMethod: UploadedMethodType.MANUAL,
        url_images: imageUrl,
        previousProductSnapshot: null,
      };
      setBumpOffer(updated);
      updateAdditionalOffers({ ...additionalOffers, bumpOffer: updated });
    },
    allowMultiple: false,
    maxImages: 1,
    maxFileSize: MAX_FILE_SIZE,
  });

  useEffect(() => {
    const b = additionalOffers.bumpOffer;
    if (!b) return;
    setBumpOffer((prev) => ({
      ...prev,
      enabled: b.enabled,
      productName: b.productName,
      price: b.price,
      description: b.description ?? '',
      image: b.image,
      isManualEntry: b.isManualEntry,
      productLink: b.productLink ?? '',
      externalId: b.externalId ?? null,
      providerId: b.providerId ?? null,
      url_images: b.url_images,
      uploadedMethod: b.uploadedMethod ?? UploadedMethodType.NONE,
      previousProductSnapshot: b.previousProductSnapshot ?? null,
      isLoading: prev.isLoading,
      error: prev.error,
    }));
    const m = b.uploadedMethod;
    if (m === UploadedMethodType.LINK) setMethod('link');
    else if (m === UploadedMethodType.MANUAL) setMethod('manual');
    else if (m === UploadedMethodType.PREVIOUS) setMethod('previous');
    else setMethod(null);
  }, [additionalOffers]);

  const handleToggle = (enabled: boolean) => {
    if (!enabled) {
      setMethod(null);
      const cleared: BumpOfferState = {
        ...bumpOffer,
        enabled: false,
        productName: '',
        price: '0.00',
        description: '',
        image: undefined,
        url_images: undefined,
        isManualEntry: false,
        productLink: '',
        uploadedMethod: UploadedMethodType.NONE,
        previousProductSnapshot: null,
      };
      setBumpOffer(cleared);
      updateAdditionalOffers({ ...additionalOffers, bumpOffer: cleared });
      return;
    }
    const next = { ...bumpOffer, enabled };
    setBumpOffer(next);
    updateAdditionalOffers({ ...additionalOffers, bumpOffer: next });
  };

  const handleChange = (field: string, value: unknown) => {
    const updated: BumpOfferState = { ...bumpOffer, [field]: value, error: '' };
    if (field === 'uploadedMethod' && value !== UploadedMethodType.PREVIOUS) {
      updated.previousProductSnapshot = null;
    }
    setBumpOffer(updated);
    updateAdditionalOffers({
      ...additionalOffers,
      bumpOffer: {
        enabled: updated.enabled,
        productName: updated.productName,
        price: updated.price,
        description: updated.description,
        image: updated.image,
        url_images: updated.url_images,
        isManualEntry: updated.isManualEntry,
        productLink: updated.productLink,
        externalId: updated.externalId,
        providerId: updated.providerId,
        uploadedMethod: updated.uploadedMethod,
        previousProductSnapshot: updated.previousProductSnapshot,
      },
    });
  };

  const setMethodAndStore = (newMethod: 'link' | 'manual' | 'previous') => {
    setMethod(newMethod);
    const map = {
      link: UploadedMethodType.LINK,
      manual: UploadedMethodType.MANUAL,
      previous: UploadedMethodType.PREVIOUS,
    };
    handleChange('uploadedMethod', map[newMethod]);
  };

  const handlePreviousSelect = (product: PreviousProduct) => {
    if (!product) return;
    const updated: BumpOfferState = {
      ...bumpOffer,
      enabled: true,
      productName: product.name,
      price: String(product.price),
      description: product.description || '',
      isManualEntry: false,
      image: '',
      url_images: product.images?.[0],
      productLink: '',
      externalId: product.id,
      providerId: 'INTERNAL_FLUXI',
      uploadedMethod: UploadedMethodType.PREVIOUS,
      previousProductSnapshot: {
        id: product.id,
        price: String(product.price),
        countryCode: product.country_code,
        currencyCode: product.currency_code,
      },
    };
    setBumpOffer(updated);
    updateAdditionalOffers({ ...additionalOffers, bumpOffer: updated });
  };

  return {
    bumpOffer,
    method,
    setMethod: setMethodAndStore,
    handleToggle,
    handleChange,
    handlePreviousSelect,
    imageUpload,
  };
}
